import { lazy, Suspense } from "react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Route, Routes } from "react-router-dom";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { Toaster } from "@/components/ui/toaster";
import { TooltipProvider } from "@/components/ui/tooltip";
import { OrderProvider } from "@/context/OrderContext";
import { I18nProvider } from "@/context/I18nContext";
import { LocaleRouter } from "@/components/LocaleRouter";

// Index is the LCP page — keep it eager so the H1 paints immediately
import Index from "./pages/Index.tsx";

// Everything else is code-split so it doesn't bloat the initial bundle
const TikTokLandingPage = lazy(() => import("./pages/TikTokLandingPage.tsx"));
const CreateOrderPage = lazy(() => import("./pages/CreateOrderPage.tsx"));
const OrderPage = lazy(() => import("./pages/OrderPage.tsx"));
const SpotifyLandingPage = lazy(() => import("./pages/SpotifyLandingPage.tsx"));
const SpotifyCreateOrderPage = lazy(() => import("./pages/SpotifyCreateOrderPage.tsx"));
const SpotifySelectTracksPage = lazy(() => import("./pages/SpotifySelectTracksPage.tsx"));
const SpotifyOrderPage = lazy(() => import("./pages/SpotifyOrderPage.tsx"));
const InstagramLandingPage = lazy(() => import("./pages/InstagramLandingPage.tsx"));
const InstagramCreateOrderPage = lazy(() => import("./pages/InstagramCreateOrderPage.tsx"));
const InstagramOrderPage = lazy(() => import("./pages/InstagramOrderPage.tsx"));
const YouTubeLandingPage = lazy(() => import("./pages/YouTubeLandingPage.tsx"));
const YouTubeCreateOrderPage = lazy(() => import("./pages/YouTubeCreateOrderPage.tsx"));
const YouTubeSelectVideosPage = lazy(() => import("./pages/YouTubeSelectVideosPage.tsx"));
const YouTubeOrderPage = lazy(() => import("./pages/YouTubeOrderPage.tsx"));
const TwitterPage = lazy(() => import("./pages/TwitterPage.tsx"));
const TwitchPage = lazy(() => import("./pages/TwitchPage.tsx"));
const FacebookPage = lazy(() => import("./pages/FacebookPage.tsx"));
const ThankYouPage = lazy(() => import("./pages/ThankYouPage.tsx"));
const DashboardPage = lazy(() => import("./pages/DashboardPage.tsx"));
const PrivacyPolicyPage = lazy(() => import("./pages/PrivacyPolicyPage.tsx"));
const TermsOfServicePage = lazy(() => import("./pages/TermsOfServicePage.tsx"));
const RefundPolicyPage = lazy(() => import("./pages/RefundPolicyPage.tsx"));
const UnsubscribePage = lazy(() => import("./pages/UnsubscribePage.tsx"));
const NotFound = lazy(() => import("./pages/NotFound.tsx"));
const AdminDashboard = lazy(() => import("./pages/AdminDashboard.tsx"));
const AdminAuthPage = lazy(() => import("./pages/AdminAuthPage.tsx"));
const AdsLandingPage = lazy(() => import("./pages/AdsLandingPage.tsx"));
const BlogListPage = lazy(() => import("./pages/BlogListPage.tsx"));
const BlogPostPage = lazy(() => import("./pages/BlogPostPage.tsx"));
const AdminBlogListPage = lazy(() => import("./pages/admin/AdminBlogListPage.tsx"));
const AdminBlogEditorPage = lazy(() => import("./pages/admin/AdminBlogEditorPage.tsx"));

const queryClient = new QueryClient();

const RouteFallback = () => (
  <div className="flex min-h-screen items-center justify-center bg-background">
    <div className="h-8 w-8 animate-spin rounded-full border-2 border-primary border-t-transparent" />
  </div>
);

/* Shared route definitions used for both / and /:lang/ */
const AppRoutes = () => (
  <>
    <Route index element={<Index />} />
    <Route path="tiktok" element={<TikTokLandingPage />} />
    <Route path="create-order/:username" element={<CreateOrderPage />} />
    <Route path="order/:username" element={<OrderPage />} />
    <Route path="spotify" element={<SpotifyLandingPage />} />
    <Route path="spotify/create" element={<SpotifyCreateOrderPage />} />
    <Route path="spotify/select-tracks" element={<SpotifySelectTracksPage />} />
    <Route path="spotify/order/:spotifyId" element={<SpotifyOrderPage />} />
    <Route path="instagram" element={<InstagramLandingPage />} />
    <Route path="instagram/create-order/:username" element={<InstagramCreateOrderPage />} />
    <Route path="instagram/order/:username" element={<InstagramOrderPage />} />
    <Route path="youtube" element={<YouTubeLandingPage />} />
    <Route path="youtube/create-order/:kind/:id" element={<YouTubeCreateOrderPage />} />
    <Route path="youtube/select-videos/:channelId" element={<YouTubeSelectVideosPage />} />
    <Route path="youtube/order/:kind/:id" element={<YouTubeOrderPage />} />
    <Route path="twitter" element={<TwitterPage />} />
    <Route path="twitch" element={<TwitchPage />} />
    <Route path="facebook" element={<FacebookPage />} />
    <Route path="thank-you" element={<ThankYouPage />} />
    <Route path="dashboard" element={<DashboardPage />} />
    <Route path="privacy" element={<PrivacyPolicyPage />} />
    <Route path="terms" element={<TermsOfServicePage />} />
    <Route path="refund-policy" element={<RefundPolicyPage />} />
    <Route path="unsubscribe" element={<UnsubscribePage />} />
    <Route path="admin" element={<AdminDashboard />} />
    <Route path="admin-signin" element={<AdminAuthPage />} />
    <Route path="go" element={<AdsLandingPage />} />
    <Route path="blog" element={<BlogListPage />} />
    <Route path="blog/:slug" element={<BlogPostPage />} />
  </>
);

const App = () => (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <I18nProvider>
      <OrderProvider>
        <Toaster />
        <Sonner />
        <BrowserRouter>
          <Suspense fallback={<RouteFallback />}>
            <Routes>
              {/* Admin CMS routes — outside LocaleRouter, no locale prefix */}
              <Route path="/admin/blog" element={<AdminBlogListPage />} />
              <Route path="/admin/blog/new" element={<AdminBlogEditorPage />} />
              <Route path="/admin/blog/edit/:id" element={<AdminBlogEditorPage />} />

              {/* Locale-prefixed routes: /es, /de, /zh, etc. */}
              <Route path="/:lang" element={<LocaleRouter />}>
                {AppRoutes()}
              </Route>

              {/* Default (English) routes without prefix */}
              <Route path="/" element={<LocaleRouter />}>
                {AppRoutes()}
              </Route>

              {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
              <Route path="*" element={<NotFound />} />
            </Routes>
          </Suspense>
        </BrowserRouter>
      </OrderProvider>
      </I18nProvider>
    </TooltipProvider>
  </QueryClientProvider>
);

export default App;
